<template>
  <PageShell
    title="Welcome"
    subtitle="Let’s get your workspace set up"
    :wide="false"
  >
    <template #actions>
      <router-link class="ll-pill ghost" to="/licenses">Login</router-link>
      <router-link class="ll-pill" to="/dashboard">Dashboard</router-link>
    </template>

    <div class="home">
      <div class="hero">
        <img src="@/assets/lotledger.png" alt="LotLedger" class="hero-logo" />
        <div class="hero-text">
          <div v-if="companyName" class="welcome">
            <div class="welcome-title">
              {{ companyName }}
              <button class="edit-btn" type="button" @click="openEdit">
                ✎ Edit
              </button>
            </div>
            <div class="welcome-sub">
              You’re ready to start adding inventory and tracking profit by lot.
            </div>

            <div class="hero-actions">
              <router-link class="ll-pill" to="/add-inventory">Add Inventory</router-link>
              <router-link class="ll-pill ghost" to="/dashboard">Go to Dashboard</router-link>
            </div>
          </div>

          <div v-else class="setup">
            <div class="setup-title">Company name</div>
            <div class="setup-sub">This shows up on reports and invoices later.</div>

            <div class="form">
              <input
                v-model="newCompanyName"
                class="input"
                placeholder="Enter company name…"
                @keyup.enter="saveCompany"
              />
              <button class="ll-pill" type="button" @click="saveCompany">
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit modal -->
      <div v-if="showEditPopup" class="overlay" @click.self="closeEdit">
        <div class="modal">
          <div class="modal-title">Edit Company Name</div>

          <input v-model="newCompanyName" class="input" />

          <div class="modal-actions">
            <button class="ll-pill ghost" type="button" @click="closeEdit">Cancel</button>
            <button class="ll-pill" type="button" @click="updateCompanyName">Save</button>
          </div>
        </div>
      </div>
    </div>
  </PageShell>
</template>

<script>
import PageShell from "@/components/layout/PageShell.vue";
import api from "@/services/api";

export default {
  components: { PageShell },
  data() {
    return {
      companyName: "",
      newCompanyName: "",
      showEditPopup: false,
    };
  },

  async mounted() {
    try {
      const response = await api.get("/company");
      if (response.data.company_name) {
        this.companyName = response.data.company_name;
      }
    } catch (error) {
      console.error("Error fetching company name:", error);
    }
  },

  methods: {
    openEdit() {
      this.newCompanyName = this.companyName;
      this.showEditPopup = true;
    },
    closeEdit() {
      this.showEditPopup = false;
    },

    async updateCompanyName() {
      try {
        const response = await api.put("/company", {
          company_name: this.newCompanyName,
        });
        this.companyName = response.data.company_name;
        this.showEditPopup = false;
      } catch (err) {
        console.error("❌ Failed to update company name:", err);
      }
    },

    async saveCompany() {
      if (!this.newCompanyName.trim()) return alert("Company name is required.");
      try {
        await api.post("/company", { company_name: this.newCompanyName });
        this.companyName = this.newCompanyName;
      } catch (error) {
        console.error("Error saving company name:", error);
      }
    },
  },
};
</script>

<style scoped>
.home {
  display: grid;
  gap: 12px;
}

.hero {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 14px;
  align-items: center;
}

.hero-logo {
  width: 100%;
  max-width: 200px;
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.75);
  background: rgba(255, 255, 255, 0.7);
  padding: 10px;
}

.welcome-title {
  font-weight: 900;
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.welcome-sub {
  margin-top: 6px;
  font-size: 13px;
  opacity: 0.8;
  font-weight: 650;
  line-height: 1.35rem;
}

.hero-actions {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.setup-title {
  font-weight: 900;
  font-size: 16px;
}

.setup-sub {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.75;
  font-weight: 650;
}

.form {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
}

.input {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(176, 196, 222, 0.9);
  background: #f6fbff;
  font-weight: 650;
  outline: none;
}

.input:focus {
  border-color: rgba(50, 123, 168, 0.55);
}

.edit-btn {
  border: 1px solid rgba(176, 196, 222, 0.9);
  background: #f6fbff;
  border-radius: 999px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 900;
  font-size: 12px;
}

.edit-btn:hover {
  background: #eef7ff;
}

/* Modal */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.28);
  display: grid;
  place-items: center;
  z-index: 50;
  padding: 16px;
}

.modal {
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0px 16px 40px rgba(0, 0, 0, 0.22);
  padding: 14px;
}

.modal-title {
  font-weight: 900;
  margin-bottom: 10px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}

@media (max-width: 980px) {
  .hero {
    grid-template-columns: 1fr;
  }
  .form {
    grid-template-columns: 1fr;
  }
}
</style>
